import { Inject, Injectable, Injector, signal } from '@angular/core';
import { Router } from '@angular/router';

import { APP_BASE_HREF } from '@angular/common';
import {
    catchError,
    firstValueFrom,
    lastValueFrom,
    Observable,
    of,
    timeout,
} from 'rxjs';
import {
    IAuthData,
    ICreatePatient,
    ICreateAuthenticationCodeResult,
    ISendAuthenticationCodeResponse,
    IResponseRegisterDB,
    ITokenAndPatientId,
    IUser,
    IPatientFind,
    IPasswordRecoveryParams,
    IDataForAuthCode,
    IContractSigningResponse,
    IResultEmailUpdate,
} from '../interfaces/patient.interfaces';
import { HttpNewService } from './application/http-new.service';
import { EventService } from './event/event.service';
import { Store } from './store.service';
import { PatientService } from './patient.service';
import {
    TAuthenticationCodeAction,
    TDeliveryChannel,
    TWebSmsAuthClient,
} from '../interfaces/patient.types';
import { LoadService } from './application/load.service';
import { CookieConsentService } from 'src/app/services/cookie-consent/cookie-consent-service';
import { Utils } from './utils';
import { ClientSessionService } from './application/client-session.service';
import { MatDialog } from '@angular/material/dialog';
import { EmailInputModalComponent } from '../modals/email-input/email-input.component';
import { ContractService } from './contract.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    public isAccessConfigNeeded = false;
    public accessConfigArray: string[] = [];
    private _patientService!: PatientService;
    public roleSystem: boolean = false;
    public accessWithoutEmail: number = 1;

    public constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private router: Router,
        private httpNew: HttpNewService,
        private eventService: EventService,
        private cookieService: CookieConsentService,
        private load: LoadService,
        private contractService: ContractService,
        private injector: Injector,
        private clientSession: ClientSessionService,
        private dialog: MatDialog,
    ) {
        this.roleSystem =
            !!this.load.configData?.CLIENT_SETTINGS.PAGES_SETTINGS.LOGIN
                .IS_ROLES_SYSTEM;
        this.accessWithoutEmail =
            this.load.configData?.CLIENT_SETTINGS.PAGES_SETTINGS.LOGIN
                .IS_REQUIRED_FILLED_EMAIL ?? 1;
    }
    // данные для странички с кодом
    private authCodeData = signal<IDataForAuthCode>({
        messageUuid: '',
        delieveryChanel: 'EMAIL',
        phoneOrMail: '',
    });
    public authCodeDataReadOnly = this.authCodeData.asReadonly();
    public updateMessageUuid(newUuid: string): void {
        this.authCodeData.update((currentData) => ({
            ...currentData, // сохраняем все старые поля
            messageUuid: newUuid, // обновляем только messageUuid
        }));
    }
    // текущее действие, в данном случае логин или рекавери
    private authAction = signal<TAuthenticationCodeAction | null>(null);
    public authActionReadonly = this.authAction.asReadonly();
    // СИГНАЛ ДЛЯ ОШИБКИ
    private authError = signal<string | null>(null);
    public authErrorReadOnly = this.authError.asReadonly();
    public updateAuthError(error: string | null): void {
        this.authError.set(error);
    }

    // СИГНАЛ ЗАГРУЗКИ loading
    private loadingAuth = signal<boolean>(false);
    public loadingReadOnly = this.loadingAuth.asReadonly();
    // Пока не используется, но пусть будет метод для обновления загрузки из компонента
    public updateLoading(loading: boolean): void {
        this.loadingAuth.set(loading);
    }
    // Сигнал для перехода сразу к recovery, если переходят по "забыли пароль" из окна смены пароля
    private toRecoveryFlag = signal<boolean>(false);
    public setRecoveryFlag(flag: boolean): void {
        // его метод для обновления извне
        this.toRecoveryFlag.set(flag);
    }

    public startAuthProcess(
        data: IDataForAuthCode,
        action: TAuthenticationCodeAction,
    ): void {
        this.authError.set(null);
        this.authCodeData.set(data);
        this.authAction.set(action);
        this.router.navigate(['auth/codes']);
    }

    public async submitAuthCode(code: string) {
        this.loadingAuth.set(true);
        this.authError.set(null);
        const data = this.authCodeData();
        const action = this.authAction();
        switch (action) {
            case 'LOGIN':
                try {
                    const response = (await lastValueFrom(
                        this.confirmAuthCode(
                            code,
                            data.delieveryChanel,
                            data.messageUuid,
                        ),
                    )) as ITokenAndPatientId;
                    if (response.patientId && response.token) {
                        this.patientService.clearPatientInfo();
                        await lastValueFrom(
                            this.patientService.fetchPatientInfo(),
                        );
                        const patient = this.patientService.currentPatient;
                        // Проверяем на наличие почты и параметра - 2 (строго нет) или 1 (ввести)
                        if (this.accessWithoutEmail === 2 && !patient?.email) {
                            throw new Error(
                                'Не указан почтовый адрес! Доступ запрещён',
                            );
                        }
                        if (this.accessWithoutEmail === 1 && !patient?.email) {
                            const dialogRef = this.dialog.open(
                                EmailInputModalComponent,
                                {
                                    data: {
                                        email: '',
                                        message:
                                            'Для продолжения работы в личном кабинете укажите Ваш почтовый адрес',
                                    },
                                },
                            );
                            const modalResult = await firstValueFrom(
                                dialogRef.afterClosed(),
                            );

                            if (modalResult && modalResult.newEmail) {
                                await this.updateEmail(modalResult.newEmail);
                            } else {
                                this.logout();
                                this.router.navigate(['auth/login']);
                                throw new Error(
                                    'Без указанного почтового адреса доступ к сервису запрещён!',
                                );
                            }
                        }
                        if (this.roleSystem) {
                            const res = await this.getAccessConfig(
                                response.patientId,
                            );
                            if (res.length <= 0) {
                                res.push(
                                    'lk_access_appointment',
                                    'lk_access_patient_data',
                                    'lk_access_see_services',
                                );
                            }
                            this.load.setAccessUserConfig(res);
                        }

                        this.storeToken(response.token);
                        this.eventService.emit('auth:login-success', response);
                        // заполняем куки при успешной авторизации
                        if (!document.cookie.includes('cookieconsent_status')) {
                            this.cookieService.setCookie(
                                'cookieconsent_status',
                                'true',
                            );
                        }
                        this.authError.set(null);
                        this.loadingAuth.set(false);
                        this.checkDocuments(response.token);
                    }
                } catch (err: unknown) {
                    let serverErr = Utils.getErrorMessage(
                        err,
                        'Ошибка при проверке кода',
                    );
                    if (serverErr === 'Invalid confirmation code')
                        serverErr =
                            'Неверный код подтверждения. Попробуйте еще раз.';
                    if (serverErr.includes('message has expired'))
                        serverErr =
                            'Срок действия кода истек. Пожалуйста, запросите новый код.';
                    this.authError.set(serverErr);
                    this.loadingAuth.set(false);
                }
                break;
            case 'RECOVERY':
                try {
                    const response = (await lastValueFrom(
                        this.confirmAuthCode(
                            code,
                            data.delieveryChanel,
                            data.messageUuid,
                        ),
                    )) as ITokenAndPatientId;
                    this.cookieService.setCookie(
                        'pa-web_Patient_Local',
                        response.token,
                    );
                    this.authError.set(null);
                    this.loadingAuth.set(false);
                    this.router.navigate(['/auth/recovery/entry-pass']);
                } catch (error: unknown) {
                    let serverErr = Utils.getErrorMessage(
                        error,
                        'Ошибка при проверке кода',
                    );
                    if (serverErr === 'Invalid confirmation code')
                        serverErr =
                            'Неверный код подтверждения. Попробуйте еще раз.';
                    if (serverErr.includes('message has expired'))
                        serverErr =
                            'Срок действия кода истек. Пожалуйста, запросите новый код.';
                    this.authError.set(serverErr);
                    this.loadingAuth.set(false);
                }
                break;
        }
    }

    private get patientService(): PatientService {
        if (!this._patientService) {
            this._patientService = this.injector.get(PatientService);
        }
        return this._patientService;
    }

    public storeToken(token: string): void {
        // Example: Store the token in localStorage or a secure cookie
        localStorage.setItem('auth_token', token);
    }
    public async login$(authData: IAuthData): Promise<IUser> {
        try {
            const result = await lastValueFrom(
                this.httpNew.post<ITokenAndPatientId>('/user/auth/login', {
                    login: authData.login,
                    password: authData.password,
                }),
            );

            if (result && result.token && result.patientId) {
                this.storeToken(result.token);

                this.patientService.clearPatientInfo();
                await lastValueFrom(this.patientService.fetchPatientInfo());
                this.eventService.emit('auth:login-success', result);

                return {
                    token: result.token,
                    patientId: result.patientId,
                };
            } else {
                throw new Error(
                    'Некорректный ответ от сервера при авторизации',
                );
            }
        } catch (err) {
            console.error('Login error:', err);
            throw err;
        }
    }

    public getAccessConfig(patientId: number): Promise<string[]> {
        return lastValueFrom(
            this.httpNew.get<Array<string>>('/user/list_access', {
                patientId: patientId,
            }),
        )
            .then((result) => {
                // console.log(result);
                return result;
            })
            .catch((err) => {
                throw err;
            });
    }
    // регистрация пациента
    public reg$(patient: ICreatePatient): Observable<IResponseRegisterDB> {
        return this.httpNew.post<IResponseRegisterDB>(
            '/user/auth/register',
            patient,
        );
    }

    public loginOk$(token: string, patientId: number): boolean {
        // this.token = token;
        // this.patientId = patientId;
        // Записываем токен в хранилище
        Store.setData('Patient', { token: token, patientId: patientId });
        return true;
    }

    /**
     * Смена пароля
     * @param pOldPassword - Старый пароль
     * @param pNewPassword - Новый пароль
     * @returns {Observable<boolean>}
     */
    public changepw(
        pOldPassword: string,
        pNewPassword: string,
        pNewPasswordRepeat: string,
    ): Observable<any> {
        return this.httpNew.post<void>('/user/change_password', {
            oldPassword: pOldPassword,
            newPassword: pNewPassword,
            newPasswordRepeat: pNewPasswordRepeat,
        });
    }
    /**
     * Смена адреса почты
     * @param newEmail - Новый адрес почты
     * @returns {Observable<IResultEmailUpdate>}
     */
    public updateEmail(newEmail: string): Observable<IResultEmailUpdate> {
        return this.httpNew.put<IResultEmailUpdate>('/user/update_email', {
            newEmail: newEmail,
        });
    }

    public sendAuthPhoneCode(
        patData: Required<
            Pick<IPatientFind, 'captchaHash' | 'client' | 'phone'>
        >,
        action: TAuthenticationCodeAction,
    ): Observable<ISendAuthenticationCodeResponse> {
        return this.httpNew.post<ISendAuthenticationCodeResponse>(
            '/user/auth/codes',
            {
                phone: patData.phone,
                captchaHash: patData.captchaHash,
                client: patData.client,
                action: action,
            },
        );
    }

    public sendPasswordRecoveryCode(
        patData: IPatientFind,
        action: TAuthenticationCodeAction,
    ): Observable<ISendAuthenticationCodeResponse> {
        return this.httpNew.post<ISendAuthenticationCodeResponse>(
            '/user/auth/codes',
            {
                lastName: patData.lastName,
                firstName: patData.firstName,
                secondName: patData.secondName ?? null,
                birthDate: patData.birthDate,
                email: patData.email ?? undefined,
                phone: patData.phone ?? undefined,
                captchaHash: patData.captchaHash,
                action: action,
                client: patData.client,
            },
        );
    }
    public sendSignContractCode(
        action: TAuthenticationCodeAction,
        client: TWebSmsAuthClient,
        contractId: number,
        text?: string | undefined,
    ): Observable<ISendAuthenticationCodeResponse> {
        return this.httpNew.post<ISendAuthenticationCodeResponse>(
            '/user/auth/codes',
            {
                action: action,
                client: client,
                contractId: contractId,
                text: text,
            },
        );
    }

    public confirmAuthCode(
        code: string,
        deliveryChannel: TDeliveryChannel,
        messageUuid: string,
        customerId?: number | null,
    ): Observable<ITokenAndPatientId | IContractSigningResponse[]> {
        return this.httpNew.post<ITokenAndPatientId>(
            `/user/auth/codes/${messageUuid}/confirmations`,
            {
                code,
                deliveryChannel,
                customerId,
            },
        );
    }

    public resendAuthenticationCode(
        messageUuid: string,
        deliveryChannel: TDeliveryChannel,
    ): Observable<ICreateAuthenticationCodeResult> {
        return this.httpNew.post<ICreateAuthenticationCodeResult>(
            `/user/auth/codes/${messageUuid}/resend`,
            { deliveryChannel },
        );
    }

    public recoveryPassword(
        passRecoveryParams: IPasswordRecoveryParams,
    ): Observable<void> {
        return this.httpNew.post<void>('/user/recovery_password', {
            newPassword: passRecoveryParams.newPassword,
            newPasswordRepeat: passRecoveryParams.newPasswordRepeat,
        });
    }

    public logout(): void {
        // Always emit logout event first
        this.eventService.emit('auth:logout');

        // Clear all client data immediately
        this.clearClientData();

        const serverLogoutTimeout = this.serverLogout().pipe(
            timeout(5000),
            catchError(() => of(true)),
        );
        lastValueFrom(serverLogoutTimeout).finally(() => {
            setTimeout(() => {
                if (this.toRecoveryFlag() === true) {
                    this.toRecoveryFlag.set(false);
                    this.redirectTo('/auth/recovery');
                } else this.redirectTo('/auth');
            }, 200);
        });
    }
    public async checkDocuments(token: string): Promise<void> {
        try {
            const docs = await lastValueFrom(
                this.contractService.getContractList(),
            );
            if (docs && docs.length > 0) {
                this.contractService.setDocuments(docs);
                await this.router.navigate(['/auth/sign-documents']);
            } else {
                this.cookieService.setCookie('pa-web_Patient_Local', token);
                await this.router.navigate(['/home']);
            }
        } catch (err: unknown) {
            const error = Utils.getErrorMessage(
                err,
                'Ошибка при загрузке документов',
            );
            console.error(error);
            this.authError.set(error);
            this.loadingAuth.set(false);
        }
    }

    protected redirectTo(uri: string) {
        // Ensure we're not already on the target route
        if (this.router.url !== uri) {
            // For absolute paths, prepend the base href
            if (uri.startsWith('/')) {
                // Use Angular's APP_BASE_HREF to construct the correct URL
                const basePath = this.appBaseHref || '/';
                // Remove trailing slash from base path and leading slash from uri to avoid double slashes
                const fullPath =
                    basePath.endsWith('/') && uri.startsWith('/')
                        ? basePath.slice(0, -1) + uri
                        : basePath + uri;

                this.router.navigate([uri]).catch((err) => {
                    console.error('Navigation error:', err);
                    // Fallback: force browser navigation with correct base path
                    window.location.href = fullPath;
                });
            } else {
                // For relative paths, use router navigation directly
                this.router.navigate([uri]).catch((err) => {
                    console.error('Navigation error:', err);
                    window.location.href = uri;
                });
            }
        }
    }
    /**
     * Процедура logout'a на стороне сервера
     * @param token
     * @returns {Observable<boolean>}
     */
    public serverLogout(): Observable<boolean> {
        return this.httpNew.post<boolean>('/user/auth/logout', null);
    }

    private clearClientData(): void {
        this.clientSession.clear();

        // Очищаем данные сервиса пациента
        try {
            this.patientService.clearPatientInfo();
        } catch (e) {
            console.warn('Не удалось очистить информацию о пациенте:', e);
        }
    }
}
